import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Package,
  TrendingUp,
  Target,
  Star,
  ChevronDown,
  ChevronUp,
  X,
  ZoomIn,
  Brain,
} from 'lucide-react';
import Card from './Card';
import EnhancedTableView from './EnhancedTableView';

interface AmazonSearchResultsProps {
  data: {
    output?: string;
    analysis?: string;
    json_response?: {
      metadata?: {
        search_term: string;
        total_personas: number;
        total_products: number;
        sharing_level: string;
        analysis_timestamp: number;
      };
      click_distribution?: {
        summary: Array<{
          product_number: number;
          percentage: number;
          selections: number;
          total_personas: number;
        }>;
      };
      products?: Array<{
        product_number: number;
        product_name: string;
        asin: string;
        source: string;
        percentage: number;
        selections: number;
        total_personas: number;
        rationale: string;
        price?: string;
        brand?: string;
        image?: string;
        performance_insights: {
          primary_appeal_factors: string[];
          supporting_factors: string[];
          attracted_personas: string[];
          average_perceived_fit: number;
          evaluations_summary: Array<{
            visual_impact: string;
            headline_analysis: string;
            perceived_fit: number;
            decision: string;
          }>;
        };
      }>;
      personas?: Array<{
        persona_number: number;
        persona_name: string;
        search_motivation: string;
        initial_scan: string;
        final_choice: {
          product_number: number;
          product_asin: string;
          main_reason: string;
          supporting_factors: string[];
          compromises: string;
          persona_connection: string;
        };
      }>;
      analysis_insights?: {
        winning_factors: string[];
        losing_factors: string[];
        key_insights: string[];
      };
      creative_performance?: {
        rankings: Array<{
          rank: number;
          product_number: number;
          votes: number;
          percentage: number;
          key_reasons: string[];
        }>;
        winning_formula: string;
      };
    };
  };
  contentData: any;
  isExporting: boolean;
}

const AmazonSearchResults: React.FC<AmazonSearchResultsProps> = ({
  data,
  contentData,
  isExporting,
}) => {
  const jsonData = data.json_response;
  const userProduct = contentData?.user_product || contentData?.audience_details?.user_product;
  const productData = contentData?.products_data || contentData?.product_data || [];
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);

  // Auto-expand all sections when exporting
  React.useEffect(() => {
    if (isExporting) {
      setExpandedSections({
        'detailed-analysis': true,
        products: true,
        personas: true,
        insights: true,
      });
    }
  }, [isExporting]);

  if (!jsonData) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No Amazon search results data available</p>
      </div>
    );
  }

  // Helper function to get product image by ASIN
  const getProductImage = (asin: string, isUserProduct: boolean = false) => {
    const product = productData.find((p: any) => p.asin === asin);
    // Only return user product image if this is actually the user's product
    if (product?.image) {
      return product.image;
    }
    if (isUserProduct && userProduct?.image) {
      return userProduct.image;
    }
    return null;
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const openImageModal = (url: string, alt: string) => {
    setSelectedImage({ url, alt });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  // Handle ESC key to close modal and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const getScoreColor = () => {
    return 'text-primary bg-primary-light border border-primary/20';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 8) return <Star className="h-4 w-4" />;
    if (score >= 6) return <TrendingUp className="h-4 w-4" />;
    return <Target className="h-4 w-4" />;
  };

  const renderProducts = () => {
    // Filter to show only the user product (the one with USER_ ASIN)
    const userProductAnalysis = jsonData.products
      ?.filter((product) => {
        // User's uploaded product has ASIN starting with "USER_"
        const isUserProduct =
          (product.asin && product.asin.startsWith('USER_')) ||
          (userProduct?.headline && product.product_name === userProduct.headline);
        return isUserProduct;
      })
      .slice(0, 1); // Only take the first match

    if (!userProductAnalysis || userProductAnalysis.length === 0) {
      return (
        <Card className="p-6 border border-gray-200">
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No user product analysis available</p>
          </div>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {userProductAnalysis.map((product) => (
          <Card key={product.product_number} className="p-6 border border-gray-200">
            <div className="flex items-start space-x-6">
              {/* Product Image */}
              <div className="flex-shrink-0">
                {(() => {
                  // User's product has ASIN starting with "USER_", Amazon products have regular ASINs
                  const imageUrl =
                    product.asin && product.asin.startsWith('USER_')
                      ? userProduct?.image // User's uploaded product
                      : product.asin
                        ? getProductImage(product.asin, false)
                        : null; // Amazon product
                  return imageUrl ? (
                    <div
                      className="relative cursor-pointer group"
                      onClick={() => openImageModal(imageUrl, product.product_name)}
                    >
                      <img
                        src={imageUrl}
                        alt={product.product_name}
                        className="w-24 h-24 object-cover rounded-lg border border-gray-200 group-hover:opacity-75 transition-opacity"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-5 h-5 text-white bg-black bg-opacity-50 rounded p-0.5" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center group relative">
                      <Package className="w-8 h-8 text-gray-400" />
                      <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[9999]">
                        No image available
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Product Details */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {product.product_name}
                    </h3>
                    <p className="text-sm text-text-secondary mt-1">
                      {product.brand && <span>{product.brand} • </span>}
                      ASIN: {product.asin}
                      {product.price && <span> • {product.price}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary">
                      {(product.percentage ?? 0).toFixed(1)}%
                    </div>
                    <p className="text-sm text-text-secondary">
                      {product.selections ?? 0} personas
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <h4 className="font-medium text-text-primary mb-2">Primary Appeal Factors</h4>
                    <ul className="space-y-1 mb-4">
                      {(product.performance_insights?.primary_appeal_factors || [])
                        .slice(
                          0,
                          expandedSections[`primary-${product.product_number}`] ? undefined : 5,
                        )
                        .map((factor, index) => (
                          <li key={index} className="text-sm text-text-medium flex items-start">
                            <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            {factor}
                          </li>
                        ))}
                    </ul>
                    {(product.performance_insights?.primary_appeal_factors || []).length > 5 && (
                      <button
                        onClick={() => toggleSection(`primary-${product.product_number}`)}
                        className="mb-4 text-sm text-primary hover:text-primary-hover flex items-center"
                      >
                        {expandedSections[`primary-${product.product_number}`] ? (
                          <>
                            Show Less <ChevronUp className="h-4 w-4 ml-1" />
                          </>
                        ) : (
                          <>
                            Show{' '}
                            {(product.performance_insights?.primary_appeal_factors || []).length -
                              5}{' '}
                            More <ChevronDown className="h-4 w-4 ml-1" />
                          </>
                        )}
                      </button>
                    )}

                    <h4 className="font-medium text-text-primary mb-2">Supporting Factors</h4>
                    <ul className="space-y-1">
                      {(product.performance_insights?.supporting_factors || [])
                        .slice(
                          0,
                          expandedSections[`supporting-${product.product_number}`] ? undefined : 5,
                        )
                        .map((factor, index) => (
                          <li key={index} className="text-sm text-text-medium flex items-start">
                            <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            {factor}
                          </li>
                        ))}
                    </ul>
                    {(product.performance_insights?.supporting_factors || []).length > 5 && (
                      <button
                        onClick={() => toggleSection(`supporting-${product.product_number}`)}
                        className="mt-2 text-sm text-primary hover:text-primary-hover flex items-center"
                      >
                        {expandedSections[`supporting-${product.product_number}`] ? (
                          <>
                            Show Less <ChevronUp className="h-4 w-4 ml-1" />
                          </>
                        ) : (
                          <>
                            Show{' '}
                            {(product.performance_insights?.supporting_factors || []).length - 5}{' '}
                            More <ChevronDown className="h-4 w-4 ml-1" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <div className="lg:col-span-1">
                    <div className="p-4 bg-gray-50 rounded-lg h-full">
                      <h4 className="font-medium text-text-primary mb-3">Rationale</h4>
                      <p className="text-sm text-text-medium leading-relaxed mb-6">
                        {product.rationale}
                      </p>

                      <div className="mb-4">
                        <h4 className="font-medium text-text-primary mb-2">
                          Average Perceived Fit
                        </h4>
                        <p className="text-sm text-text-secondary mb-3">
                          How well this product fits the target personas (0-10 scale)
                        </p>
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${getScoreColor()}`}
                        >
                          {getScoreIcon(product.performance_insights?.average_perceived_fit ?? 0)}
                          <span className="ml-1">
                            {(product.performance_insights?.average_perceived_fit ?? 0).toFixed(1)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-text-primary mb-2">Attracted Personas</h4>
                        <p className="text-sm text-text-secondary mb-3">
                          Personas who showed interest in this product
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(product.performance_insights?.attracted_personas || []).map(
                            (persona, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 text-text-medium text-xs rounded"
                              >
                                {persona}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Why People Might Choose Competitors - Inside Product Card */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-text-primary mb-4">
                    Why People Might Choose Competitors
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-text-primary mb-2">
                        Conversion Concerns for Your Product
                      </h5>
                      <ul className="space-y-2 text-sm text-text-medium">
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Competitors may have clearer value propositions in their headlines
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Product images may not fully demonstrate real-world usage scenarios
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Missing trust signals or quality indicators in visual presentation
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Target audience alignment could be clearer from product presentation
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Differentiation from competitors may not be immediately obvious
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-medium text-text-primary mb-2">
                        Competitive Positioning Insights
                      </h5>
                      <ul className="space-y-2 text-sm text-text-medium">
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Competitors with higher market share likely have stronger brand
                          recognition
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Price positioning may influence customer decision-making
                        </li>
                        <li className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                          Product features and benefits communication could be optimized
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderPersonas = () => {
    const personas = jsonData.personas || [];
    const headers = [
      'Persona',
      'Motivation',
      'Initial Scan',
      'Selected Product',
      'Main Reason',
      'Supporting Factors',
      'Persona Connection',
      'Compromises',
    ];
    const data = personas.map((persona) => {
      const selectedProduct = jsonData.products?.find(
        (p) => p.product_number === persona.final_choice?.product_number,
      );
      return [
        `${persona.persona_name} (#${persona.persona_number})`,
        persona.search_motivation || '-',
        persona.initial_scan || '-',
        selectedProduct?.product_name || `Product ${persona.final_choice?.product_number ?? '-'}`,
        persona.final_choice?.main_reason || '-',
        (persona.final_choice?.supporting_factors || []).join(', '),
        persona.final_choice?.persona_connection || '-',
        persona.final_choice?.compromises || '-',
      ];
    });
    return (
      <EnhancedTableView
        headers={headers}
        data={data}
        title="Persona Responses"
        maxInitialRows={5}
        showFullContent={true}
        showExportButtons={!isExporting}
      />
    );
  };

  const renderCreativePerformance = () => {
    const creativeData = jsonData.creative_performance;

    if (!creativeData || !creativeData.rankings || creativeData.rankings.length === 0) {
      return null;
    }

    // Remove duplicates from rankings based on product ASIN/name
    const uniqueRankings = creativeData.rankings.reduce((acc: any[], ranking: any) => {
      const product = jsonData.products?.find((p) => p.product_number === ranking.product_number);
      const productIdentifier = product?.asin || product?.product_name || ranking.product_number;

      // Check if we already have this product
      const existingIndex = acc.findIndex((r) => {
        const existingProduct = jsonData.products?.find(
          (p) => p.product_number === r.product_number,
        );
        const existingIdentifier =
          existingProduct?.asin || existingProduct?.product_name || r.product_number;
        return existingIdentifier === productIdentifier;
      });

      if (existingIndex === -1) {
        acc.push(ranking);
      } else {
        // Keep the one with higher votes/percentage
        if (ranking.votes > acc[existingIndex].votes) {
          acc[existingIndex] = ranking;
        }
      }

      return acc;
    }, []);

    return (
      <Card className="p-6 border border-gray-200 mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-6">
          Creative Performance Ranking
        </h3>

        {/* Performance Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left p-4 font-medium text-text-primary">Rank</th>
                <th className="text-left p-4 font-medium text-text-primary">Product</th>
                <th className="text-left p-4 font-medium text-text-primary">Votes (% of Total)</th>
                <th className="text-left p-4 font-medium text-text-primary">
                  Key Reasons for Performance
                </th>
              </tr>
            </thead>
            <tbody>
              {uniqueRankings
                .sort((a, b) => b.votes - a.votes)
                .map((ranking, index) => {
                  // Find the product data for this ranking
                  const product = jsonData.products?.find(
                    (p) => p.product_number === ranking.product_number,
                  );
                  // User's product has a fake ASIN starting with "USER_" or matches the headline
                  const isUserProduct =
                    (product?.asin && product.asin.startsWith('USER_')) ||
                    (userProduct?.headline && product?.product_name === userProduct.headline);
                  const actualRank = index + 1;

                  return (
                    <tr
                      key={`${ranking.product_number}-${index}`}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="p-4">
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                            actualRank === 1 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {actualRank}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-start space-x-3">
                          {/* Image */}
                          {(() => {
                            // User's product has ASIN starting with "USER_", Amazon products have regular ASINs
                            const imageUrl =
                              product?.asin && product.asin.startsWith('USER_')
                                ? userProduct?.image // User's uploaded product
                                : product?.asin
                                  ? getProductImage(product.asin, false)
                                  : null; // Amazon products

                            return imageUrl ? (
                              <div
                                className="relative cursor-pointer group flex-shrink-0"
                                onClick={() =>
                                  openImageModal(
                                    imageUrl,
                                    product?.product_name || `Product ${ranking.product_number}`,
                                  )
                                }
                              >
                                <img
                                  src={imageUrl}
                                  alt={product?.product_name || `Product ${ranking.product_number}`}
                                  className="w-16 h-16 object-cover rounded border border-gray-200 group-hover:opacity-75 transition-opacity"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <ZoomIn className="w-4 h-4 text-white bg-black bg-opacity-50 rounded p-0.5" />
                                </div>
                              </div>
                            ) : (
                              <div className="w-16 h-16 bg-gray-50 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            );
                          })()}

                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {isUserProduct && (
                                <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                                  Your Product
                                </span>
                              )}
                              <span className="text-xs text-text-secondary">
                                Product {ranking.product_number}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-text-primary line-clamp-2">
                              {product?.product_name ||
                                userProduct?.headline ||
                                `Product ${ranking.product_number}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-lg font-semibold text-text-primary">
                          {ranking.votes} ({ranking.percentage.toFixed(1)}%)
                        </div>
                        <div className="text-sm text-text-secondary">personas selected</div>
                      </td>
                      <td className="p-4">
                        <ul className="space-y-1">
                          {ranking.key_reasons.map((reason: string, index: number) => (
                            <li key={index} className="text-sm text-text-medium flex items-start">
                              <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Winning Formula */}
        {creativeData.winning_formula && (
          <div className="mt-6 p-4 bg-primary-light rounded-lg border border-primary/20">
            <h4 className="font-medium text-text-primary mb-2">
              <Star className="inline-block w-4 h-4 mr-2 text-primary" />
              Winning Formula
            </h4>
            <p className="text-sm text-text-medium">{creativeData.winning_formula}</p>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-8">
      {/* Creative Performance Ranking - Always visible if available */}
      {renderCreativePerformance()}

      {/* Detailed Analysis - Using exact pattern from simulation results */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('detailed-analysis')}
          className="flex items-center justify-between w-full text-left p-4 bg-gradient-to-r from-gray-50 to-white hover:from-primary-light hover:to-white transition-all duration-200 group"
        >
          <div className="flex items-center">
            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 mr-3 group-hover:border-primary/20 transition-colors">
              <Brain className="h-4 w-4 text-primary" />
            </div>
            <div>
              <span className="text-base font-medium text-text-primary">Detailed Analysis</span>
              <p className="text-xs text-text-secondary mt-0.5">
                In-depth insights and recommendations
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-text-secondary font-medium">
              {expandedSections['detailed-analysis'] ? 'Hide Details' : 'View Details'}
            </span>
            {expandedSections['detailed-analysis'] ? (
              <ChevronUp className="h-4 w-4 text-primary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-primary" />
            )}
          </div>
        </button>

        {expandedSections['detailed-analysis'] && (
          <div className="border-t border-gray-100 p-6 bg-white">
            <div className="space-y-8">
              {/* Product Analysis - No headers, direct content */}
              {renderProducts()}

              {/* Persona Table - No headers, direct content */}
              {renderPersonas()}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal - Rendered via Portal to avoid container constraints */}
      {selectedImage &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-w-4xl max-h-[90vh] w-full flex flex-col">
              {/* Close button */}
              <button
                onClick={closeImageModal}
                className="absolute top-4 right-4 z-10 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-full p-2 shadow-lg transition-colors border border-gray-200"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Image Container */}
              <div className="flex-1 flex items-center justify-center p-4 pt-16 pb-16">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {/* Image info bar */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <div className="text-center">
                  <h3 className="text-base font-medium text-gray-900">{selectedImage.alt}</h3>
                  <p className="text-xs text-gray-500 mt-1">Press ESC or click outside to close</p>
                </div>
              </div>
            </div>

            {/* Backdrop click to close */}
            <div className="absolute inset-0 -z-10" onClick={closeImageModal} />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AmazonSearchResults;
